import { useQuery } from '@tanstack/react-query';
import { BackgroundJobStateFilter } from 'src/api/FiretrolApiClient';
import { backgroundJobsApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { hasPollTickFailed, toCancelToken } from 'src/api/queries/queryClient';

const FAST_POLL_INTERVAL = 10_000;

// Servers and recurring schedules change on the order of minutes, not seconds.
const SLOW_POLL_INTERVAL = 30_000;

const pollFast = (state: { status: string }): number | false =>
  hasPollTickFailed(state) ? false : FAST_POLL_INTERVAL;

const pollSlow = (state: { status: string }): number | false =>
  hasPollTickFailed(state) ? false : SLOW_POLL_INTERVAL;

export function useBackgroundJobOverview() {
  return useQuery({
    queryKey: queryKeys.backgroundJobs.overview(),
    queryFn: ({ signal }) =>
      backgroundJobsApiClient.getOverview(toCancelToken(signal)),
    refetchInterval: (query) => pollFast(query.state),
    meta: { errorContext: 'background job overview' },
  });
}

export function useBackgroundJobServers() {
  return useQuery({
    queryKey: queryKeys.backgroundJobs.servers(),
    queryFn: ({ signal }) =>
      backgroundJobsApiClient.getServers(toCancelToken(signal)),
    refetchInterval: (query) => pollSlow(query.state),
    meta: { errorContext: 'background job servers' },
  });
}

export function useBackgroundJobs(
  state: BackgroundJobStateFilter,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: queryKeys.backgroundJobs.jobs(state, page, pageSize),
    queryFn: ({ signal }) =>
      backgroundJobsApiClient.getJobs(
        state,
        page,
        pageSize,
        toCancelToken(signal),
      ),
    refetchInterval: (query) => pollFast(query.state),
    meta: { errorContext: 'background jobs' },
  });
}

export function useBackgroundJob(id: string | null) {
  return useQuery({
    queryKey: queryKeys.backgroundJobs.job(id ?? ''),
    queryFn: ({ signal }) =>
      backgroundJobsApiClient.getJob(id!, toCancelToken(signal)),
    enabled: id !== null,
    meta: { errorContext: 'background job detail' },
  });
}

export function useRecurringBackgroundJobs() {
  return useQuery({
    queryKey: queryKeys.backgroundJobs.recurring(),
    queryFn: ({ signal }) =>
      backgroundJobsApiClient.getRecurringJobs(toCancelToken(signal)),
    refetchInterval: (query) => pollSlow(query.state),
    meta: { errorContext: 'recurring background jobs' },
  });
}
